import { useMemo } from 'react';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { useUserStore } from '../store/useUserStore';
import {
  dismissHint,
  emptyFlags,
  flagsFor,
  hasSeenTour,
  isHintDismissed,
  markTourSeen,
  mergeFlags,
  setChecklist,
  userKey,
  type OnboardingFlags,
} from './flags';

interface OnboardingState {
  /** Flags per user (see `userKey`), so a shared browser keeps each person's progress. */
  users: Record<string, OnboardingFlags>;
  markTourSeen: (key: string, tourId: string, version?: number) => void;
  dismissHint: (key: string, hintId: string) => void;
  setChecklist: (
    key: string,
    patch: Partial<Pick<OnboardingFlags, 'checklistDismissed' | 'checklistCollapsed'>>,
  ) => void;
  mergeFlags: (key: string, incoming: OnboardingFlags) => void;
  reset: (key: string) => void;
}

const EMPTY_FLAGS = emptyFlags();

const update = (
  users: Record<string, OnboardingFlags>,
  key: string,
  fn: (flags: OnboardingFlags) => OnboardingFlags,
) => {
  const current = flagsFor(users, key);
  const next = fn(current);
  return next === current ? users : { ...users, [key]: next };
};

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set) => ({
      users: {},
      markTourSeen: (key, tourId, version = 1) =>
        set((s) => ({ users: update(s.users, key, (f) => markTourSeen(f, tourId, version)) })),
      dismissHint: (key, hintId) =>
        set((s) => ({ users: update(s.users, key, (f) => dismissHint(f, hintId)) })),
      setChecklist: (key, patch) =>
        set((s) => ({ users: update(s.users, key, (f) => setChecklist(f, patch)) })),
      mergeFlags: (key, incoming) =>
        set((s) => ({ users: update(s.users, key, (f) => mergeFlags(f, incoming)) })),
      reset: (key) =>
        set((s) => ({ users: { ...s.users, [key]: { ...emptyFlags(), updatedAt: Date.now() } } })),
    }),
    {
      name: 'musicade-onboarding',
      version: 1,
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export const useOnboardingKey = (): string => useUserStore((s) => userKey(s.user?.id));

/** Flags and actions for the signed-in user (or the anonymous visitor). */
export const useOnboarding = () => {
  const key = useOnboardingKey();
  const flags = useOnboardingStore((s) => s.users[key]);

  return useMemo(() => {
    const store = useOnboardingStore.getState;
    const resolved = flags ?? EMPTY_FLAGS;
    return {
      key,
      flags: resolved,
      hasSeenTour: (tourId: string, version = 1) => hasSeenTour(resolved, tourId, version),
      isHintDismissed: (hintId: string) => isHintDismissed(resolved, hintId),
      markTourSeen: (tourId: string, version = 1) => store().markTourSeen(key, tourId, version),
      dismissHint: (hintId: string) => store().dismissHint(key, hintId),
      setChecklist: (
        patch: Partial<Pick<OnboardingFlags, 'checklistDismissed' | 'checklistCollapsed'>>,
      ) => store().setChecklist(key, patch),
      reset: () => store().reset(key),
    };
  }, [key, flags]);
};
